import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faEnvelope,
  faMobileAlt,
  faImage,
  faShieldAlt,
  faShareAlt,
  faUser,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import AnalysisDetailsSimple from "../../scamdetector/email/components/AnalysisDetailsSimple";
import SmsAnalysisDetailsSimple from "../../scamdetector/text/components/SmsAnalysisDetailsSimple";
import ImageAnalysisDetails from "../../scamdetector/image/ImageAnalysisDetails";
import SocialMediaAnalysisDetails from "../../scamdetector/socialmedia/components/AnalysisDetails";
import { useTranslation } from "../../../../i18n/LanguageContext";

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const TYPE_META = {
  EMAIL_ANALYSIS: {
    labelKey: "usermgmt.analyses.detailTitle.email",
    icon: faEnvelope,
  },
  SOCIAL_MEDIA_ANALYSIS: {
    labelKey: "usermgmt.analyses.detailTitle.socialMedia",
    icon: faShareAlt,
  },
  SMS_ANALYSIS: {
    labelKey: "usermgmt.analyses.detailTitle.sms",
    icon: faMobileAlt,
  },
  IMAGE_ANALYSIS: {
    labelKey: "usermgmt.analyses.detailTitle.image",
    icon: faImage,
  },
};

function parseRiskFactors(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch {
    // not JSON — try comma-separated
  }
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Adapt a social media analysis item into the shape SocialMediaAnalysisDetails expects. */
function buildSocialMediaAnalysis(raw) {
  return {
    id: raw.id,
    businessName: raw.businessName || raw.platform || null,
    websiteUrl: raw.businessWebsite || null,
    analysisStatus: raw.analysisStatus || null,
    isScam: raw.isScam,
    confidence: raw.confidence,
    explanation: raw.explanation,
    suggestions: raw.suggestions,
    scamType: raw.scamType,
    platform: raw.platform,
    username: raw.username,
    postContent: raw.analysisInput,
    userInputs: null,
    dataSufficiency: null,
    missingDataPoints: [],
  };
}

/** Adapt a raw EmailAnalysisRecord (email) into the shape AnalysisDetailsSimple expects. */
function buildEmailAnalysis(raw) {
  return {
    id: raw.id,
    key: String(raw.id ?? ""),
    analysis: {
      analysisResult: {
        isScam: raw.isScam,
        confidence: raw.confidence,
        reasoning: raw.explanation,
        suggestions: raw.suggestions,
        suspiciousElements: parseRiskFactors(raw.riskFactors),
        scamType: raw.scamType,
        analyzedEmailContent: {
          subject: raw.subject || raw.analysisTitle,
          senderEmail: raw.senderEmail,
          from: raw.senderEmail || raw.analysisDetail,
          emailBody: raw.analysisInput,
        },
        htmlEmailContent: null,
      },
    },
  };
}

function formatEmailDate(dateStr) {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}

/** Adapt a raw ForwardedSmsAnalysis item into the shape SmsAnalysisDetailsSimple expects. */
function buildSmsAnalysis(raw) {
  let urlAnalyses = [];
  if (raw.analysisResultJson) {
    try {
      const parsed = JSON.parse(raw.analysisResultJson);
      if (Array.isArray(parsed?.urlAnalyses)) {
        urlAnalyses = parsed.urlAnalyses;
      }
    } catch {
      // ignore
    }
  }
  return {
    id: raw.id,
    key: raw.id?.toString(),
    messageContent: raw.messageContent,
    userContext: raw.userContext,
    analysis: {
      analysisResult: {
        isScam: raw.isScam,
        confidence: raw.confidence,
        scamType: raw.scamType,
        reasoning: raw.analysisExplanation,
        suggestions: raw.suggestions,
        riskFactors: parseRiskFactors(raw.riskFactors),
        urlAnalyses,
        analyzedTextContent: raw.messageContent,
      },
    },
  };
}

/** Adapt a raw UserScamReport (image) into the shape ImageAnalysisDetails expects. */
function buildImageAnalysis(raw) {
  return {
    id: raw.id,
    isScam: raw.isScam,
    confidence: raw.confidence,
    securityVerdict: raw.riskLevel,
    hasMalware: false,
    processingError: null,
    imageUrl: null,
    fileName: raw.analysisTitle || "Uploaded Image",
    analysisResult: {
      isScam: raw.isScam,
      confidence: raw.confidence,
      scamType: raw.scamType || raw.riskLevel,
      explanation: raw.explanation,
      riskFactors: parseRiskFactors(raw.riskFactors),
      suggestions: raw.suggestions,
      urlAnalyses: [],
    },
  };
}

/**
 * MemberAnalysisDetailSheet
 * Slide-in side sheet (portal) showing the full details of a single saved analysis,
 * using the same member-facing detail components.
 */
function MemberAnalysisDetailSheet({ analysis, analysisType, onClose }) {
  const { t } = useTranslation();
  const sheetRef = useRef(null);
  const prevFocusRef = useRef(null);
  const [showOriginalMessageInline, setShowOriginalMessageInline] =
    useState(false);

  // Capture focus, move into sheet on open
  useEffect(() => {
    prevFocusRef.current = document.activeElement;
    requestAnimationFrame(() => {
      const first = sheetRef.current?.querySelector(FOCUSABLE);
      first?.focus();
    });
    return () => {
      prevFocusRef.current?.focus();
    };
  }, []);

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Trap focus + Escape
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key !== "Tab") return;
    const nodes = Array.from(
      sheetRef.current?.querySelectorAll(FOCUSABLE) ?? [],
    );
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const meta = TYPE_META[analysisType] ?? {
    labelKey: "usermgmt.analyses.verdict.analysis",
    icon: faShieldAlt,
  };

  let detailContent;
  let emailMetadata = null;
  if (analysisType === "EMAIL_ANALYSIS") {
    const adapted = buildEmailAnalysis(analysis);
    const subject = analysis.subject || analysis.analysisTitle;
    const sender = analysis.senderName
      ? `${analysis.senderName} <${analysis.senderEmail}>`
      : analysis.senderEmail || analysis.analysisDetail;
    const receivedDate = formatEmailDate(analysis.createdAt);
    if (subject || sender || receivedDate) {
      emailMetadata = (
        <div className="um-email-meta-card">
          {subject && (
            <div className="um-email-meta-row">
              <FontAwesomeIcon
                icon={faEnvelope}
                className="um-email-meta-icon"
              />
              <span className="um-email-meta-label">Subject:</span>
              <span className="um-email-meta-value">{subject}</span>
            </div>
          )}
          {sender && (
            <div className="um-email-meta-row">
              <FontAwesomeIcon icon={faUser} className="um-email-meta-icon" />
              <span className="um-email-meta-label">From:</span>
              <span className="um-email-meta-value">{sender}</span>
            </div>
          )}
          {receivedDate && (
            <div className="um-email-meta-row">
              <FontAwesomeIcon
                icon={faCalendarAlt}
                className="um-email-meta-icon"
              />
              <span className="um-email-meta-label">Analyzed:</span>
              <span className="um-email-meta-value">{receivedDate}</span>
            </div>
          )}
          {analysis.analysisInput && (
            <details className="um-email-body-details">
              <summary>View email body</summary>
              <pre className="um-email-body-content">
                {analysis.analysisInput}
              </pre>
            </details>
          )}
        </div>
      );
    }
    detailContent = (
      <AnalysisDetailsSimple
        selectedAnalysis={adapted}
        reportId={analysis.reportId ?? null}
        onReanalyzeWithContext={null}
        readOnly={true}
      />
    );
  } else if (analysisType === "SMS_ANALYSIS") {
    const adapted = buildSmsAnalysis(analysis);
    detailContent = (
      <SmsAnalysisDetailsSimple
        selectedAnalysis={adapted}
        reportId={analysis.reportId ?? null}
        showOriginalMessageInline={showOriginalMessageInline}
        onToggleOriginalMessageInline={setShowOriginalMessageInline}
        onReanalyzeWithContext={null}
        readOnly={true}
      />
    );
  } else if (analysisType === "IMAGE_ANALYSIS") {
    const adapted = buildImageAnalysis(analysis);
    detailContent = (
      <ImageAnalysisDetails
        analysis={adapted}
        onBack={onClose}
        onDelete={null}
      />
    );
  } else if (analysisType === "SOCIAL_MEDIA_ANALYSIS") {
    const adapted = buildSocialMediaAnalysis(analysis);
    detailContent = (
      <SocialMediaAnalysisDetails
        analysisResult={null}
        selectedHistoryItem={adapted}
        onPreviousCase={null}
        onNextCase={null}
        canNavigatePrevious={false}
        canNavigateNext={false}
        reportId={analysis.reportId ?? null}
      />
    );
  }

  const sheet = (
    <div className="um-sheet-root">
      {/* Backdrop */}
      <div className="um-sheet-overlay" onClick={onClose} aria-hidden="true" />

      {/* Sheet panel */}
      <div
        ref={sheetRef}
        className="um-sheet-panel um-analysis-detail-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Analysis details"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="um-sheet-header">
          <div className="um-sheet-header-left">
            <span className="um-sheet-header-icon">
              <FontAwesomeIcon icon={meta.icon} />
            </span>
            <h2 className="um-sheet-title">{t(meta.labelKey)}</h2>
          </div>
          <button
            className="um-sheet-close"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body — embeds the member-facing detail component */}
        <div className="um-sheet-body um-analysis-detail-body um-embedded-detail">
          {emailMetadata}
          {detailContent}
        </div>
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}

export default MemberAnalysisDetailSheet;
